/**
 * Functional Abilities Section for Resident Detail Dialog
 *
 * Displays a condensed comparison of Section GG functional ability scores
 * between Therapy (single evaluation) and Nursing (configurable N-day
 * window of AM/PM shift observations ending today). Highlights
 * discrepancies and decline trends.
 *
 * Reference: figma_reference/fundamental_abilities_data/
 */

import React, { useState } from "react";
import { Activity, ChevronDown } from "lucide-react";
import type {
  FunctionalAbilitiesResponse,
  FunctionalAbilityRow,
  FunctionalCode,
  NursingDay,
} from "../../../types/residents";
import {
  FUNCTIONAL_CODE_LABELS,
  SELF_CARE_ABILITIES,
  MOBILITY_ABILITIES,
  isFunctionalScore,
} from "../../../types/residents";
import { FunctionalAbilityScoreCell } from "../../molecules/FunctionalAbilityScoreCell";
import { FunctionalAbilitySparkline } from "../../molecules/FunctionalAbilitySparkline";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";

// Mirrors the grid's New Insights window selector in ResidentsTable so the
// drill-down can default to — and match — whichever range the user picked
// on the grid (NETSOL-11382).
const TREND_DAY_OPTIONS = [3, 5, 7, 14, 21, 30];

// ── Category lookup sets ───────────────────────────────────────────────

const SELF_CARE_SET: ReadonlySet<string> = new Set(SELF_CARE_ABILITIES);
const MOBILITY_SET: ReadonlySet<string> = new Set(MOBILITY_ABILITIES);

// ── Business logic helpers ──────────────────────────────────────────────

function hasDiscrepancy(
  therapy: FunctionalCode,
  nursing: FunctionalCode
): boolean {
  return therapy !== null && nursing !== null && therapy !== nursing;
}

/**
 * Returns the ordered sequence of nursing scores (AM then PM for each day).
 * Null/non-functional codes are filtered out — a gap doesn't break the trend.
 */
function orderedNursingScores(row: FunctionalAbilityRow): number[] {
  const scores: number[] = [];
  for (const day of row.nursing) {
    if (isFunctionalScore(day.AM)) scores.push(day.AM);
    if (isFunctionalScore(day.PM)) scores.push(day.PM);
  }
  return scores;
}

function hasNursingDecline(row: FunctionalAbilityRow): boolean {
  const scores = orderedNursingScores(row);
  if (scores.length < 2) return false;
  return scores[scores.length - 1] < scores[0];
}

function hasTherapyDecline(row: FunctionalAbilityRow): boolean {
  const scores = orderedNursingScores(row);
  if (scores.length === 0 || !isFunctionalScore(row.therapy.score)) return false;
  return scores[scores.length - 1] < row.therapy.score;
}

function allNursingScores(row: FunctionalAbilityRow): FunctionalCode[] {
  return row.nursing.flatMap((day: NursingDay) => [day.AM, day.PM]);
}

function allNursingDayLabels(row: FunctionalAbilityRow): string[] {
  return row.nursing.map((day: NursingDay) => day.nursing_date ?? "");
}

// ── Inline sub-components ───────────────────────────────────────────────

function TrendCell({
  nursing,
  dayLabels,
}: {
  nursing: FunctionalCode[];
  dayLabels: string[];
}) {
  const nursingNums: number[] = [];
  const labels: string[] = [];

  nursing.forEach((code, i) => {
    if (isFunctionalScore(code)) {
      const dayIdx = Math.floor(i / 2);
      const shift = i % 2 === 0 ? "AM" : "PM";
      const date = dayLabels[dayIdx];
      const desc = FUNCTIONAL_CODE_LABELS[code] || String(code);
      nursingNums.push(code);
      labels.push(`${date} ${shift}: ${code} - ${desc}`);
    }
  });

  if (nursingNums.length < 2) return null;

  const declining =
    nursingNums[nursingNums.length - 1] < nursingNums[0];
  const color = declining ? "#c4292e" : "#0369a1";

  return (
    <FunctionalAbilitySparkline
      values={nursingNums}
      color={color}
      labels={labels}
    />
  );
}

// ── Section Props ───────────────────────────────────────────────────────

interface FunctionalAbilitiesSectionProps {
  data: FunctionalAbilitiesResponse | null;
  isLoading: boolean;
  error: Error | null;
  days: number;
  onDaysChange: (days: number) => void;
}

// ── Abilities Table (renders rows for a single category) ───────────────

function AbilitiesTable({
  rows,
  numDays,
}: {
  rows: FunctionalAbilityRow[];
  numDays: number;
}) {
  const dayIndices = Array.from({ length: numDays }, (_, i) => i);
  const discrepancyCount = rows.reduce(
    (c, row) =>
      c +
      allNursingScores(row).filter((n) => hasDiscrepancy(row.therapy.score, n))
        .length,
    0
  );
  const declineCount = rows.filter(hasNursingDecline).length;

  return (
    <>
      {/* Header Stats */}
      <div className="flex items-center justify-end mb-2">
        <div className="flex gap-4">
          <div className="text-center">
            <span className="block text-lg font-extrabold text-amber-600">
              {discrepancyCount}
            </span>
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Discrepancies
            </span>
          </div>
          <div className="text-center">
            <span className="block text-lg font-extrabold text-red-600">
              {declineCount}
            </span>
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Nursing Declines
            </span>
          </div>
        </div>
      </div>

      {/* Legend Bar */}
      <div className="flex items-center gap-2.5 flex-wrap text-xs mb-4 py-2 px-3 bg-gray-50 border border-gray-200 rounded">
        <span className="font-bold text-gray-600 uppercase tracking-wide text-xs">
          Scale
        </span>
        <span className="w-px h-4 bg-gray-300" />
        {[6, 5, 4, 3, 2, 1].map((code) => (
          <span key={code} className="flex items-center gap-1">
            <FunctionalAbilityScoreCell
              code={code as FunctionalCode}
              small
            />
            <span className="text-gray-500 text-xs">
              {FUNCTIONAL_CODE_LABELS[code]}
            </span>
          </span>
        ))}
        <span className="w-px h-4 bg-gray-300" />
        <span className="flex items-center gap-1 text-gray-500 font-medium">
          <FunctionalAbilityScoreCell code={9 as FunctionalCode} small />
          Not scored
        </span>
        <span className="flex items-center gap-1 text-gray-500 font-medium">
          <span className="text-gray-300 font-semibold min-w-4 text-center">
            &mdash;
          </span>
          No data
        </span>
        <span className="w-px h-4 bg-gray-300" />
        <span className="flex items-center gap-1 text-gray-500 font-medium">
          <span
            className="w-3.5 h-3.5 rounded-sm border border-amber-300/50"
            style={{ backgroundColor: "#fde1c3" }}
          />
          Discrepancy
        </span>
        <span className="flex items-center gap-1 text-gray-500 font-medium">
          <span className="text-red-600 text-xs">&#9660;</span>
          Decline
        </span>
      </div>

      {/* Comparison Table — horizontally scrollable on small screens */}
      <div className="overflow-x-auto">
        <table className="border-collapse text-sm min-w-175 w-full">
          <thead>
            <tr className="bg-gray-50">
              <th
                className="text-left px-3 py-2 border-b border-r border-gray-200 whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wide"
                rowSpan={3}
              >
                Functional Ability
              </th>
              <th
                className="text-center px-3 py-2 border-b border-l border-r border-gray-200"
                rowSpan={3}
              >
                <span className="block text-xs font-bold text-[#00838f] uppercase tracking-wide">
                  Therapy
                </span>
                <span className="block text-xs text-gray-400 mt-0.5">
                  {rows[0]?.therapy.therapy_date ?? ""}
                </span>
              </th>
              <th
                className="text-center px-3 py-2 border-b border-l border-gray-200 text-xs font-bold text-[#0369a1] uppercase tracking-wide"
                colSpan={numDays * 2 + 1}
              >
                Nursing
              </th>
            </tr>
            <tr className="bg-gray-50">
              {dayIndices.map((i) => (
                <th
                  key={i}
                  className="text-center px-2 py-1.5 border-b-2 border-l border-gray-200"
                  colSpan={2}
                >
                  <span className="block text-xs font-bold text-[#00263e]">
                    {rows[0]?.nursing[i]?.nursing_date ?? ""}
                  </span>
                </th>
              ))}
              <th
                className="text-center px-2 py-1.5 border-b-2 border-l border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wider min-w-20"
                rowSpan={2}
              >
                Trend
              </th>
            </tr>
            <tr className="bg-gray-50">
              {dayIndices.map((di) =>
                (["AM", "PM"] as const).map((shift, si) => (
                  <th
                    key={`${di}-${si}`}
                    className="text-center px-2 py-1 border-b-2 border-gray-200 text-[10px] font-semibold text-gray-400 uppercase tracking-wide"
                  >
                    {shift}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              const nursingDecline = hasNursingDecline(row);
              const therapyDecline = hasTherapyDecline(row);

              return (
                <tr
                  key={row.ability}
                  className={`${
                    rowIdx % 2 !== 0 ? "bg-gray-50" : ""
                  } hover:bg-gray-50/80 transition-colors`}
                >
                  <td className="px-3 py-2 font-semibold text-sm text-[#00263e] whitespace-nowrap border-r border-r-gray-200 border-b border-b-gray-100">
                    {row.ability}
                  </td>
                  <td className="px-2 py-2 text-center align-middle border-l border-l-gray-200 border-r border-r-gray-200 border-b border-b-gray-100 bg-gray-50/50">
                    <FunctionalAbilityScoreCell code={row.therapy.score} />
                    {therapyDecline && (
                      <span
                        className="inline-flex items-center text-red-600 text-xs font-bold ml-1"
                        title="Therapy score is higher than latest nursing score"
                      >
                        &#9660;
                      </span>
                    )}
                  </td>
                  {row.nursing.slice(0, numDays).map((day, di) =>
                    ([day.AM, day.PM] as FunctionalCode[]).map(
                      (nursingCode, si) => {
                        const disc = hasDiscrepancy(
                          row.therapy.score,
                          nursingCode
                        );
                        return (
                          <td
                            key={`${di}-${si}`}
                            className={`px-2 py-2 text-center align-middle border-b border-gray-100 ${
                              si === 0 ? "border-l border-gray-200" : ""
                            } ${disc ? "bg-[#fde1c3]" : ""}`}
                          >
                            <FunctionalAbilityScoreCell code={nursingCode} />
                          </td>
                        );
                      }
                    )
                  )}
                  <td className="px-2 py-2 text-center align-middle border-l border-gray-200 border-b border-gray-100">
                    <TrendCell
                      nursing={allNursingScores(row)}
                      dayLabels={allNursingDayLabels(row)}
                    />
                    {nursingDecline && (
                      <span
                        className="inline-flex items-center text-red-600 text-xs font-bold ml-1"
                        title="Nursing score declined across the window"
                      >
                        &#9660;
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Main Component ──────────────────────────────────────────────────────

export const FunctionalAbilitiesSection: React.FC<
  FunctionalAbilitiesSectionProps
> = ({ data, isLoading, error, days, onDaysChange }) => {
  const [activeTab, setActiveTab] = useState("self-care");

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          Loading functional abilities...
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-4 text-center text-destructive">
          Error loading functional abilities: {error.message}
        </div>
      );
    }

    if (!data || data.abilities.length === 0) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          No functional abilities data available
        </div>
      );
    }

    const selfCareRows = data.abilities.filter((row) =>
      SELF_CARE_SET.has(row.ability)
    );
    const mobilityRows = data.abilities.filter((row) =>
      MOBILITY_SET.has(row.ability)
    );

    return (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="self-care">
            Self-Care (GG0130)
          </TabsTrigger>
          <TabsTrigger value="mobility">
            Mobility (GG0170)
          </TabsTrigger>
        </TabsList>
        <TabsContent value="self-care" className="p-2">
          <AbilitiesTable rows={selfCareRows} numDays={days} />
        </TabsContent>
        <TabsContent value="mobility" className="p-2">
          <AbilitiesTable rows={mobilityRows} numDays={days} />
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <Activity className="w-5 h-5 text-[#228189]" />
        <h2 className="text-lg font-semibold text-gray-900">
          Functional Abilities
        </h2>
        <div className="relative ml-1">
          <select
            value={days}
            onChange={(e) => onDaysChange(Number(e.target.value))}
            className="appearance-none pl-3 pr-7 py-1 border border-solid border-[#228189]/30 rounded-full text-xs font-semibold text-[#228189] bg-[#228189]/5 cursor-pointer hover:bg-[#228189]/10 transition-colors focus:outline-none focus:ring-1 focus:ring-[#228189]/40"
            aria-label="Select trend window"
          >
            {TREND_DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>Last {d} Days</option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#228189] pointer-events-none" />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden p-2">
        {renderContent()}
      </div>
    </div>
  );
};
